import { z } from 'zod';
/**
 * Product analytics contracts.
 *
 * Every metric is an executable DEFINITION, not just a number: numerator,
 * denominator, timeframe and exclusions are declared here and rendered next
 * to the value wherever it appears, so an executive can reproduce any figure
 * from its documented lineage. Metrics are computed from Control's own
 * synthetic operational tables — never from clinical content, which cannot
 * enter Control at any boundary.
 */
export const METRIC_KEYS = [
    'activation_rate',
    'time_to_value_days',
    'fleet_adoption_current_release',
    'retention_rate',
    'support_burden_per_org',
    'sla_attainment',
    'expansion_signals',
    'invoice_collection_rate',
];
export const MetricKeySchema = z.enum(METRIC_KEYS);
export const METRIC_DEFINITIONS = {
    activation_rate: {
        key: 'activation_rate',
        title: 'Activation rate',
        numerator: 'Organisations that reached lifecycle state live',
        denominator: 'Organisations that entered onboarding',
        timeframe: 'Trailing 180 days by onboarding start date',
        exclusions: 'Archived organisations; cancelled onboarding instances',
        sourceOfTruth: 'organisation_lifecycle_events, onboarding_instances',
        unit: 'percent',
    },
    time_to_value_days: {
        key: 'time_to_value_days',
        title: 'Median time to value',
        numerator: 'Median days from onboarding start to go-live',
        denominator: '1',
        timeframe: 'Organisations that went live in the trailing 180 days',
        exclusions: 'Waived-step go-lives are included and flagged, never hidden',
        sourceOfTruth: 'onboarding_instances (started_at → went_live_at)',
        unit: 'days',
    },
    fleet_adoption_current_release: {
        key: 'fleet_adoption_current_release',
        title: 'Fleet on current release',
        numerator: 'Connected installations on the latest published release',
        denominator: 'Connected installations',
        timeframe: 'Point in time at computation',
        exclusions: 'Offline installations (reported separately, never assumed)',
        sourceOfTruth: 'fleet_installations, releases',
        unit: 'percent',
    },
    retention_rate: {
        key: 'retention_rate',
        title: 'Organisation retention',
        numerator: 'Live organisations still live at period end',
        denominator: 'Organisations live at period start',
        timeframe: 'Trailing 365 days',
        exclusions: 'Organisations that entered the portfolio inside the period',
        sourceOfTruth: 'organisation_lifecycle_events',
        unit: 'percent',
    },
    support_burden_per_org: {
        key: 'support_burden_per_org',
        title: 'Support burden per live organisation',
        numerator: 'Support cases opened',
        denominator: 'Live organisations',
        timeframe: 'Trailing 90 days',
        exclusions: 'Severity s4 feature requests',
        sourceOfTruth: 'support_cases, organisations',
        unit: 'ratio',
    },
    sla_attainment: {
        key: 'sla_attainment',
        title: 'Support SLA attainment',
        numerator: 'Cases acknowledged within their SLA',
        denominator: 'Cases opened',
        timeframe: 'Trailing 90 days',
        exclusions: 'Cases still open and inside their acknowledgement window',
        sourceOfTruth: 'support_cases (acknowledged_at vs ack_due_at)',
        unit: 'percent',
    },
    expansion_signals: {
        key: 'expansion_signals',
        title: 'Open expansion signals',
        numerator: 'Unresolved entitlement drift cases indicating usage above contract',
        denominator: '1',
        timeframe: 'Point in time at computation',
        exclusions: 'Drift indicating under-usage (a churn signal, shown separately)',
        sourceOfTruth: 'drift_cases',
        unit: 'count',
    },
    invoice_collection_rate: {
        key: 'invoice_collection_rate',
        title: 'Invoice collection rate',
        numerator: 'Invoices settled on or before their grace date',
        denominator: 'Invoices issued and past due date',
        timeframe: 'Trailing 180 days by issue date',
        exclusions: 'Draft and void invoices',
        sourceOfTruth: 'b2b_invoices',
        unit: 'percent',
    },
};
/** A computed metric value plus everything needed to trust or reject it. */
export const MetricValueSchema = z.object({
    key: MetricKeySchema,
    title: z.string(),
    value: z.number().nullable(),
    unit: z.enum(['percent', 'days', 'count', 'ratio']),
    numerator: z.string(),
    denominator: z.string(),
    timeframe: z.string(),
    exclusions: z.string(),
    sourceOfTruth: z.string(),
    /** Raw counts behind the figure, so it is reproducible by inspection. */
    numeratorValue: z.number().nullable(),
    denominatorValue: z.number().nullable(),
    computedAt: z.iso.datetime(),
    /** True when a source table is empty or a dependency is missing. */
    incomplete: z.boolean(),
    incompleteReason: z.string().nullable(),
});
export const MetricsResponseSchema = z.object({
    items: z.array(MetricValueSchema),
    computedAt: z.iso.datetime(),
});
//# sourceMappingURL=analytics.js.map